<template>
  <div v-if="isActive" class="tutorial-overlay">
    <!-- 遮罩层 -->
    <div class="tutorial-backdrop" @click="handleSkip"></div>

    <!-- 高亮区域 -->
    <div
      v-if="highlightStyle"
      class="tutorial-highlight"
      :style="highlightStyle"
    ></div>

    <!-- 提示框 -->
    <div
      v-if="tooltipStyle"
      class="tutorial-tooltip"
      :class="[`placement-${currentStep.placement}`]"
      :style="tooltipStyle"
    >
      <div class="tutorial-tooltip-header">
        <div class="tutorial-step-counter">
          {{ currentStepIndex + 1 }} / {{ steps.length }}
        </div>
        <button class="tutorial-close-btn" @click="handleSkip">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <div class="tutorial-tooltip-content">
        <h3 class="tutorial-title">{{ currentStepData.title ? $t(currentStepData.title) : '' }}</h3>
        <p class="tutorial-description">{{ currentStepData.content ? $t(currentStepData.content) : '' }}</p>
      </div>

      <div class="tutorial-tooltip-footer">
        <button
          v-if="currentStepIndex > 0"
          class="btn btn-outline btn-sm"
          @click="handlePrev"
        >
          {{ $t('tutorial.common.prev') }}
        </button>

        <div class="tutorial-dots">
          <span
            v-for="(step, index) in steps"
            :key="step.id"
            class="tutorial-dot"
            :class="{ active: index === currentStepIndex }"
          ></span>
        </div>

        <div class="tutorial-actions-right">
          <button class="btn btn-text btn-sm" @click="handleSkip">
            {{ $t('tutorial.common.skip') }}
          </button>
          <button
            class="btn btn-primary btn-sm"
            @click="handleNext"
          >
            {{ isLastStep ? $t('tutorial.common.finish') : $t('tutorial.common.next') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TutorialGuide',

  props: {
    // 是否激活教程
    active: {
      type: Boolean,
      default: false
    },

    // 教程步骤
    steps: {
      type: Array,
      required: true,
      default: () => []
    },

    // 当前步骤索引
    currentStep: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      highlightStyle: null,
      tooltipStyle: null,
      resizeObserver: null
    }
  },

  computed: {
    isActive() {
      return this.active && this.steps.length > 0
    },

    currentStepIndex() {
      return Math.max(0, Math.min(this.currentStep, this.steps.length - 1))
    },

    currentStepData() {
      return this.steps[this.currentStepIndex] || {}
    },

    isLastStep() {
      return this.currentStepIndex === this.steps.length - 1
    }
  },

  watch: {
    isActive(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updatePosition()
          this.setupResizeObserver()
        })
      } else {
        this.cleanupResizeObserver()
      }
    },

    currentStepIndex() {
      if (this.isActive) {
        this.$nextTick(() => {
          this.updatePosition()
        })
      }
    }
  },

  mounted() {
    if (this.isActive) {
      this.$nextTick(() => {
        this.updatePosition()
        this.setupResizeObserver()
      })
    }
  },

  beforeUnmount() {
    this.cleanupResizeObserver()
  },

  methods: {
    /**
     * 更新高亮区域和提示框位置
     */
    updatePosition() {
      if (!this.currentStepData.target) {
        this.highlightStyle = null
        this.tooltipStyle = null
        return
      }

      const targetElement = document.querySelector(this.currentStepData.target)
      if (!targetElement) {
        console.warn(`Tutorial target element not found: ${this.currentStepData.target}`)
        this.highlightStyle = null
        this.tooltipStyle = null
        return
      }

      const rect = targetElement.getBoundingClientRect()
      const padding = this.currentStepData.highlightPadding || 10
      const placement = this.currentStepData.placement || 'bottom'

      // 设置高亮区域样式
      this.highlightStyle = {
        top: `${rect.top - padding}px`,
        left: `${rect.left - padding}px`,
        width: `${rect.width + padding * 2}px`,
        height: `${rect.height + padding * 2}px`,
        borderRadius: '8px'
      }

      // 计算提示框位置
      this.tooltipStyle = this.calculateTooltipPosition(rect, placement, padding)
    },

    /**
     * 计算提示框位置
     */
    calculateTooltipPosition(targetRect, placement, padding) {
      const tooltipWidth = 360
      const tooltipGap = 16
      let top, left

      switch (placement) {
        case 'top':
          top = targetRect.top - padding - tooltipGap - 200
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
          break

        case 'bottom':
          top = targetRect.bottom + padding + tooltipGap
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
          break

        case 'left':
          top = targetRect.top + targetRect.height / 2 - 100
          left = targetRect.left - padding - tooltipGap - tooltipWidth
          break

        case 'right':
          top = targetRect.top + targetRect.height / 2 - 100
          left = targetRect.right + padding + tooltipGap
          break

        default:
          top = targetRect.bottom + padding + tooltipGap
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
      }

      // 确保提示框在视口内
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (left < 20) left = 20
      if (left + tooltipWidth > viewportWidth - 20) {
        left = viewportWidth - tooltipWidth - 20
      }

      if (top < 20) top = 20
      if (top > viewportHeight - 250) {
        top = viewportHeight - 250
      }

      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${tooltipWidth}px`
      }
    },

    /**
     * 设置ResizeObserver
     */
    setupResizeObserver() {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          this.updatePosition()
        })

        if (this.currentStepData.target) {
          const targetElement = document.querySelector(this.currentStepData.target)
          if (targetElement) {
            this.resizeObserver.observe(targetElement)
          }
        }
      }

      window.addEventListener('resize', this.updatePosition)
      window.addEventListener('scroll', this.updatePosition)
    },

    /**
     * 清理ResizeObserver
     */
    cleanupResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      window.removeEventListener('resize', this.updatePosition)
      window.removeEventListener('scroll', this.updatePosition)
    },

    /**
     * 下一步
     */
    handleNext() {
      if (this.isLastStep) {
        this.$emit('finish')
      } else {
        this.$emit('next')
      }
    },

    /**
     * 上一步
     */
    handlePrev() {
      this.$emit('prev')
    },

    /**
     * 跳过教程
     */
    handleSkip() {
      this.$emit('skip')
    }
  }
}
</script>

<style scoped>
.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;
}

.tutorial-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  pointer-events: auto;
}

.tutorial-highlight {
  position: absolute;
  background-color: transparent;
  border: 2px solid #4461F2;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  transition: all 0.3s ease;
  z-index: 10000;
}

.tutorial-tooltip {
  position: absolute;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  padding: 20px;
  pointer-events: auto;
  z-index: 10001;
  transition: all 0.3s ease;
}

.tutorial-tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.tutorial-step-counter {
  font-size: 14px;
  font-weight: 600;
  color: #4461F2;
  background-color: rgba(68, 97, 242, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
}

.tutorial-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #64748b;
  transition: color 0.2s;
}

.tutorial-close-btn:hover {
  color: #334155;
}

.tutorial-tooltip-content {
  margin-bottom: 20px;
}

.tutorial-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 12px 0;
}

.tutorial-description {
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
}

.tutorial-tooltip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.tutorial-actions-right {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.tutorial-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}

.tutorial-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e2e8f0;
  transition: all 0.3s;
}

.tutorial-dot.active {
  width: 24px;
  border-radius: 4px;
  background-color: #4461F2;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .tutorial-tooltip {
    width: calc(100% - 40px) !important;
    left: 20px !important;
    max-width: 360px;
  }

  .tutorial-tooltip-footer {
    flex-wrap: wrap;
  }

  .tutorial-actions-right {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
